<template>
  <Modal
    class="project-analysis-text-modal modal-themes-add"
    header-tag="h3"
    :isEditThemes="true"
    :ref="modalRef"
    :show="show"
    :reset-listener="resetListener"
    v-if="textAnalysisObject"
    @close="closeModal"
  >
    <template #header>
      <VisibleText>Edit themes: Add </VisibleText>
      {{ type }}
    </template>
    <ThemeList
      ref="themeListRef"
      :type="type"
      :propReadOnly="propReadOnly"
      :responses="responses"
      :selection="selection"
      :selectedNewThemeUUID="selectedNewThemeUUID"
      :themes="themeList"
      :textAnalysisObject="textAnalysisObject"
      :should-focus-first-theme="shouldFocusFirstTheme"
      :key="updateKey"
      :selectedTextModalResponseId="selectedTextModalResponseId"
      :duplicateIndex="duplicateIndex"
      :includeSmartCoverage="includeSmartCoverage"
      :sortedThemes="sortedThemes"
      :hasPreProcessedText="hasPreProcessedText"
      @selectedRespondent="updateSelectedRespondent"
      @updatingSpinner="updateSpinner"
      @update-theme-title="renameThemeTitle(themeList[$event.index], $event.title)"
      @updated="unsavedChanges = true"
      @applyPatches="applyPatches($event)"
      @removeTheme="removeTheme($event)"
    />
    <template #footer>
      <div class="modal-footer__options">
        <button
          class="btn-default"
          role="button"
          @click.stop="closeModal"
          :disabled="
            !unsavedChanges || textAnalysisObject.showSpinner || awaitingLoading || propReadOnly
          "
        >
          <div v-if="!awaitingLoading">
            <VisibleText>Close window</VisibleText>
          </div>
          <div v-else>
            <VisibleText>Wait for coverage to complete</VisibleText>
          </div>
        </button>
        <button
          class="btn-create btn-secondary"
          role="button"
          @click.stop="createNewTheme()"
          :disabled="propReadOnly"
        >
          <VisibleText>Create new theme</VisibleText>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
// Mixins
import ThemeListMixin from '../Mixins/themeListMixin.js';
import textAnalysisMixin from '../../Mixins/textAnalysisMixin.js';
import themeColorsMixin from '../../Mixins/themeColorsMixin.js';

export default {
  name: 'ThemesAdd',
  mixins: [ThemeListMixin, textAnalysisMixin, themeColorsMixin],
  props: {
    type: {
      default: () => 'keyword',
      type: String,
    },
    selection: {
      type: Object,
      required: false,
      default: () => {},
    },
    autoCreateTheme: {
      default: () => false,
      type: Boolean,
    },
    autoCreateThemeTitle: {
      default: () => 'unnamed theme',
      type: String,
    },
    autoCreateThemeKeywords: {
      default: () => [],
      type: Array,
    },
    show: {
      default: () => false,
      type: Boolean,
    },
    textAnalysisObject: {
      type: Object,
      required: false,
      default: () => {},
    },
    responses: {
      default: () => [],
      type: Array,
    },
    selectedTextModalResponseId: {
      type: String,
      required: false,
      default: () => '',
    },
    hasPreProcessedText: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    includeSmartCoverage: {
      type: String,
      default: () => 'exclude',
    },
    sortedThemes: {
      type: Array,
      default: () => [],
    },
    propReadOnly: {
      default: () => false,
      type: Boolean,
    },
  },
  data() {
    return {
      isAutoCreateEnabled: true,
      selectedNewThemeUUID: '',
      duplicateIndex: -1,
      spinnerQueue: [],
    };
  },
  created() {
    if (this.textAnalysisObject) {
      if (this.sortedThemes.length) {
        this.themeList = this.deepCloneObj(this.sortedThemes);
      }
    }
  },
  computed: {
    awaitingLoading() {
      if (this.spinnerQueue.length) {
        return true;
      }
      return false;
    },
    parentThemesLength() {
      let length = 0;
      if (this.textAnalysisObject) {
        if (this.sortedThemes) {
          length = this.sortedThemes.length;
        }
      }
      return length;
    },
    foundChanges() {
      let changedThemes = [];
      if (!this.themeList || !this.themeList.length) return changedThemes;
      let themes = this.deepCloneObj(this.themeList);
      if (!this.sortedThemes.length && themes.length) return themes;

      // -1 means a change has been made but update all themes regardless of index
      // if (!this.sortedThemes.length && themes.length) return [-1]
      // if (this.sortedThemes.length !== this.themesList.length) return [-1]
      if (this.sortedThemes) {
        for (let x = 0; x < themes.length; x++) {
          // Each theme has to find at least one matching UUID
          let foundMatchingUUID = false;
          for (let i = 0; i < this.sortedThemes.length; i++) {
            if (themes[x]._id === this.sortedThemes[i]._id) {
              foundMatchingUUID = true;
              // name changed
              if (themes[x].name !== this.sortedThemes[i].name) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
              // new coverage
              if (themes[x].coverage.total != this.sortedThemes[i].coverage.total) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
              // new filtered coverage
              if (
                themes[x].filtered_coverage.total != this.sortedThemes[i].filtered_coverage.total
              ) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
              // new keyword length
              if (
                themes[x].keyword_objects.length !== this.sortedThemes[i].keyword_objects.length
              ) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
              let foundMatch = [];
              // new keyword found
              for (let y = 0; y < themes[x].keyword_objects.length; y++) {
                const modelKeyword = themes[x].keyword_objects[y].keyword.toLowerCase();

                // normalize theme keywords
                const themeKeywords = (this.sortedThemes[i].keyword_objects || []).map((ko) =>
                  ko.keyword.toLowerCase(),
                );

                if (themeKeywords.includes(modelKeyword)) {
                  foundMatch.push(1);
                } else {
                  foundMatch.push(0);
                }
              }

              if (foundMatch.includes(0)) {
                if (!changedThemes.includes(i)) {
                  changedThemes.push(i);
                }
              }
            }
          }
          // entirely new theme, refresh all to be safe, can be optimized later
          if (!foundMatchingUUID) {
            changedThemes.push(themes[x]);
          }
        }
      }
      return changedThemes;
    },
  },
  methods: {
    updateTextObject(cleanedProject) {
      let object = {
        project: cleanedProject,
        scroll: 'main',
      };
      this.$emit('updateTextObject', object);
    },
    updateSpinner(val) {
      this.spinnerQueue = val;
    },
    refreshThemesFromParent() {
      this.themeList = this.deepCloneObj(this.sortedThemes);
      this.$refs.themeListRef.refreshThemesFromParent();
    },
    async applyPatches(patches) {
      for (const patch of patches) {
        await this.$emit('updateTheme', patch);
      }
    },
    async removeTheme(theme) {
      await this.$emit('removeTheme', theme);
    },
    updateSelectedRespondent(selectedID, selection) {
      if (!selection || !selection.content) return;

      const keyword = this.trimContent(selection.content);
      const selectedThemeId = selectedID.$oid || selectedID;

      // Emit add for the selected theme if needed
      const selectedTheme = this.themeList.find((t) => t._id === selectedThemeId);

      const alreadyHasKeyword =
        selectedTheme.keyword_objects &&
        selectedTheme.keyword_objects.some(
          (ko) => ko.keyword.trim().toLowerCase() === keyword.trim().toLowerCase(),
        );

      if (!alreadyHasKeyword) {
        this.$emit('updateTheme', {
          _id: selectedThemeId,
          keywords_to_add: [keyword],
        });
      }

      // Emit remove for all other themes that contain this keyword
      this.themeList.forEach((theme) => {
        if (theme._id === selectedThemeId) return;

        const hasKeyword =
          theme.keyword_objects &&
          theme.keyword_objects.some(
            (ko) => ko.keyword.trim().toLowerCase() === keyword.trim().toLowerCase(),
          );

        if (hasKeyword) {
          this.$emit('updateTheme', {
            _id: theme._id,
            keywords_to_remove: [keyword],
          });
        }
      });
    },

    async createNewTheme() {
      const content = this.trimContent(this.selection?.content) || '';
      // 1. Create the theme with no initial keywords
      const newTheme = await this.createTheme(content, [], {});

      if (newTheme) {
        // 2. Add the content as a keyword AFTER creation
        this.$emit('updateTheme', {
          _id: newTheme._id,
          keywords_to_add: [content],
        });

        // 3. Refresh UI
        this.$emit('refreshTheme', newTheme._id);
      }
    },

    trimContent(content) {
      return content.replace(/[.,!;?\)\("]+$/g, '');
    },

    async renameThemeTitle(theme, title) {
      if (this.themeList) {
        let themes = this.deepCloneObj(this.themeList);
        if (typeof title !== 'string' || title.trim() === '') {
          title = 'unnamed theme';
        }
        let foundIndex = -1;
        let foundExisting = false;
        for (let i = 0; i < themes.length; i++) {
          if (themes[i]._id === theme._id) {
            foundIndex = i;
          }
          if (themes[i].name && themes[i].name.toLowerCase() == title.toLowerCase()) {
            foundExisting = true;
          }
        }
        if (foundIndex > -1 && !foundExisting) {
          themes[foundIndex].name = title;
          this.themeList = themes;
          this.$emit('updateExistingThemeTitle', themes[foundIndex], title);
        }
        if (foundIndex > -1 && foundExisting) {
          this.duplicateIndex = foundIndex;
          setTimeout(() => {
            this.duplicateIndex = -1;
          }, 6000);
        }
      }
    },
  },
  watch: {
    // TODO what's this for?
    // updateKey: function (val) {
    //   if (!val) return;
    //   console.log("this create?")
    //   // auto create a theme
    //   // should be called only when auto create enabled
    //   // -- usually once per component update when autoCreateTheme = True
    //   if (this.isAutoCreateEnabled && this.autoCreateTheme) {
    //     // disable auto create to prevent re-creating the same theme again
    //     // when the component updates
    //     this.isAutoCreateEnabled = false;
    //     console.log("this.selection?.content", this.selection?.content)
    //     this.createTheme(
    //       "gia",
    //       this.filterBannedKeywords(this.autoCreateThemeKeywords),
    //     );
    //   }
    // },
    show: function (val) {
      if (!val) {
        this.isAutoCreateEnabled = true;
        this.themeList = [];
      } else {
        if (this.sortedThemes.length) {
          this.themeList = this.deepCloneObj(this.sortedThemes);
        }
      }
    },
    parentThemesLength: async function (val) {
      if (!val) return;
      if (this.textAnalysisObject) {
        if (this.sortedThemes.length) {
          this.themeList = this.deepCloneObj(this.sortedThemes);
        }
      }
    },
  },
};
</script>
